import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock, useEnterTransition } from "@blog/spa-common/components/Modal";

// Overlay modal shared by the Guide and ATS panels. Owns the shared shell:
// backdrop (click-to-close), Escape-to-close, and the sticky header with a
// mono title + close button. Body content is passed as children.
//
// No usa el <Modal> de @blog/spa-common —aunque sí sus hooks— porque el
// tailwind.config de esta app NO escanea packages/spa-common: las clases de
// utilidad tienen que estar escritas en un fichero que Tailwind mire, y éste
// lo es.
export function Modal({
  ariaLabel,
  icon,
  title,
  onClose,
  children,
}: {
  ariaLabel: string;
  icon: ReactNode;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const shown = useEnterTransition(mounted);
  useBodyScrollLock();

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        shown ? "opacity-100" : "opacity-0"
      }`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded border border-line bg-panel shadow-glow transition duration-200 ease-out motion-reduce:transition-none ${
          shown ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
        }`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-panel px-4 py-2 font-mono text-xs uppercase tracking-widest text-mute">
          <span>
            <span className="text-accent">{icon}</span> {title}
          </span>
          <button onClick={onClose} className="shrink-0 text-2xl leading-none text-mute hover:text-danger" aria-label="cerrar">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
